import { novoRegistroBase } from '../db/tipos'
import type { AjustePedido } from '../db/tipos'
import { gravarLocalEEnfileirar } from '../sync/outbox'

export type Culpa = 'fabrica' | 'cliente'

// Lista FECHADA, fechada com a usuaria em 2026-07-28. Texto livre foi
// descartado de proposito: sem codigo nao vira metrica.
//
// "produto descontinuado" foi descartado por ela ("se a fabrica para com o
// produto ele nem vem mais na tabela"), e "credito recusado" foi reclassificado
// de cliente para FABRICA ("quem recusou foi a fabrica").
export const MOTIVOS_AJUSTE: ReadonlyArray<{ codigo: string; rotulo: string; culpa: Culpa | null }> = [
  { codigo: 'fabrica_sem_estoque', rotulo: 'Fábrica sem estoque / produto em falta', culpa: 'fabrica' },
  { codigo: 'nao_atingiu_minimo', rotulo: 'Não atingiu o mínimo da fábrica', culpa: 'fabrica' },
  { codigo: 'sem_rota', rotulo: 'Pedido sem rota', culpa: 'fabrica' },
  { codigo: 'credito_recusado', rotulo: 'Crédito do cliente recusado', culpa: 'fabrica' },
  { codigo: 'cliente_desistiu_item', rotulo: 'Cliente desistiu do item antes de faturar', culpa: 'cliente' },
  { codigo: 'correcao_minha', rotulo: 'Correção minha — não foi corte', culpa: null },
]

export const MOTIVO_CORRECAO = 'correcao_minha'

// Motivos de ACRESCIMO, fechados com ela em 2026-07-29. Nasceram de um beco sem
// saida achado no smoke dela: o aviso de pedido minimo aparecia depois do corte
// e nao havia como acrescentar produto para completar o valor.
//
// Sao uma lista SEPARADA porque os motivos de corte falam de perda -- justificar
// um acrescimo com "fabrica sem estoque" transformaria "vender mais" em "a
// fabrica cortou" e sujaria a metrica. Nenhum deles tem culpa: acrescentar nunca
// e venda perdida. E o motivo que agora guarda a fronteira entre "refletir o
// corte da fabrica" e "vender mais" -- antes quem guardava era a proibicao.
const ACRESCIMO_PROPRIOS: ReadonlyArray<{ codigo: string; rotulo: string; culpa: Culpa | null }> = [
  { codigo: 'completar_minimo', rotulo: 'Completar o mínimo da fábrica', culpa: null },
  { codigo: 'cliente_pediu_mais', rotulo: 'Cliente pediu mais', culpa: null },
]

// "Correcao minha" serve nos dois lados, e vem da lista de cima para nao existir
// em duas versoes que possam divergir.
export const MOTIVOS_ACRESCIMO: ReadonlyArray<{
  codigo: string; rotulo: string; culpa: Culpa | null
}> = [...ACRESCIMO_PROPRIOS, ...MOTIVOS_AJUSTE.filter((m) => m.codigo === MOTIVO_CORRECAO)]

export function ehMotivoDeAcrescimo(codigo: string): boolean {
  return MOTIVOS_ACRESCIMO.some((m) => m.codigo === codigo)
}

// Motivos de DEVOLUCAO, fechados com ela em 2026-07-29 (spec do Ciclo 2, sec. 5).
// Terceira lista, pelo mesmo motivo que existe a segunda: devolucao e perda de
// outra natureza -- a mercadoria foi produzida, faturada e VOLTOU. Justificar
// devolucao com "fabrica sem estoque" juntaria dois momentos distintos do ciclo
// na mesma metrica.
//
// "Nao achou o endereco" e UM motivo so (ela decidiu nao separar cadastro errado
// de transportadora perdida). A culpa 'fabrica' e decisao minha, comunicada a
// ela, seguindo o criterio que ela mesma usou em "credito recusado" e "sem
// rota": falha na ponta da entrega fica com a fabrica.
export const MOTIVOS_DEVOLUCAO: ReadonlyArray<{
  codigo: string; rotulo: string; culpa: Culpa | null
}> = [
  { codigo: 'devolucao_cliente', rotulo: 'Devolução de item pelo cliente', culpa: 'cliente' },
  { codigo: 'avaria_entrega', rotulo: 'Avaria na entrega', culpa: 'fabrica' },
  { codigo: 'nao_achou_endereco', rotulo: 'Não achou o endereço', culpa: 'fabrica' },
]

export function ehMotivoDeDevolucao(codigo: string): boolean {
  return MOTIVOS_DEVOLUCAO.some((m) => m.codigo === codigo)
}

function acharMotivo(codigo: string) {
  return (
    MOTIVOS_AJUSTE.find((m) => m.codigo === codigo) ??
    ACRESCIMO_PROPRIOS.find((m) => m.codigo === codigo) ??
    MOTIVOS_DEVOLUCAO.find((m) => m.codigo === codigo)
  )
}

// A culpa e DEDUZIDA do motivo, nunca gravada. Motivo: a usuaria reclassificou
// "credito_recusado" durante o proprio brainstorming -- se a culpa estivesse
// gravada em cada linha, os ajustes velhos contariam contra o cliente e os
// novos contra a fabrica, misturando dois criterios para sempre. Deduzindo,
// mudar de ideia reorganiza o historico inteiro junto.
//
// O preco disso, assumido na spec: reclassificar um motivo muda numeros do
// passado. E o certo para indicador de gestao, e seria errado para documento
// fiscal -- este nao e um documento fiscal.
export function culpaDoMotivo(codigo: string): Culpa | null {
  return acharMotivo(codigo)?.culpa ?? null
}

export function contaComoPerda(codigo: string): boolean {
  return culpaDoMotivo(codigo) !== null
}

// Codigo desconhecido volta como veio: cancelamentos anteriores a este ciclo
// gravaram texto livre em sub_pedidos.motivo_cancelamento.
export function rotuloDoMotivo(codigo: string): string {
  return acharMotivo(codigo)?.rotulo ?? codigo
}

// Uma linha por ajuste, nunca sobrescrita: dois cortes no mesmo item sao duas
// linhas, com datas diferentes.
export async function registrarAjuste(
  dados: Omit<AjustePedido, 'id' | 'criado_em' | 'atualizado_em' | 'apagado_em'>,
): Promise<void> {
  await gravarLocalEEnfileirar('ajustes_pedido', { ...novoRegistroBase(), ...dados })
}
